import type { AtendenteDTO, ConversaDetalheDTO, MensagemDTO } from '@viniko/shared';
import { formatarTelefone } from '@viniko/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRightLeft,
  Bell,
  BellOff,
  Bot,
  CircleCheck,
  EllipsisVertical,
  Mail,
  Trash2,
  Ban,
  Download,
  Pencil,
  Tag,
  PanelRight,
  Pin,
  PinOff,
  RotateCcw,
  UserCheck,
} from 'lucide-react';
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AvatarContato, StatusTexto, useToast } from '../../componentes/ui';
import { patch, post } from '../../lib/api';
import { chaves, useConversa, useJanelaAberta } from '../../lib/consultas';
import { rotuloDia } from '../../lib/tempo';
import { Balao } from './Balao';
import { Compositor } from './Compositor';
import { ModalTransferir } from './ModalTransferir';
import { ModalApagar, ModalBloquear, ModalEditarContato, ModalEtiquetas } from './ModaisContato';
import { PainelContato } from './PainelContato';
import { PreviaEnvio } from './PreviaEnvio';
import { SimuladorCliente } from './SimuladorCliente';

export function Chat({
  id,
  eu,
  voltarPara = '/conversas',
}: {
  id: string;
  eu: AtendenteDTO;
  voltarPara?: string;
}) {
  const conversa = useConversa(id);
  const qc = useQueryClient();
  const toast = useToast();
  const janelaAbertaAgora = useJanelaAberta();
  const [transferir, setTransferir] = useState(false);
  const [menu, setMenu] = useState(false);
  const [modal, setModal] = useState<'editar' | 'etiquetas' | 'bloquear' | 'apagar' | null>(null);
  const [painel, setPainel] = useState(false);
  const [respondendo, setRespondendo] = useState<MensagemDTO | null>(null);
  const [anexos, setAnexos] = useState<File[] | null>(null);
  const [arrastando, setArrastando] = useState(false);
  const fimRef = useRef<HTMLDivElement>(null);
  const c = conversa.data;

  const qtdMensagens = c?.mensagens.length ?? 0;
  useLayoutEffect(() => {
    fimRef.current?.scrollIntoView({ block: 'end' });
  }, [qtdMensagens]);

  useEffect(() => setMenu(false), [id]);

  if (conversa.isError) {
    return (
      <main className="chat">
        <p className="lista-vazia">{(conversa.error as Error).message}</p>
      </main>
    );
  }
  if (!c) return <main className="chat" aria-busy />;

  const minha = c.status === 'atendimento' && c.atendente?.id === eu.id;
  const podeGerir =
    minha ||
    (c.status === 'atendimento' && eu.papel === 'admin') ||
    c.status === 'fila' ||
    c.status === 'bot';

  async function acao(caminho: string, msgOk: string) {
    try {
      await post(`/api/conversas/${id}/${caminho}`);
      toast(msgOk);
      qc.invalidateQueries({ queryKey: chaves.conversa(id) });
    } catch (e) {
      toast((e as Error).message, 'erro');
    }
  }

  async function opcao(corpo: Record<string, boolean>, msgOk: string) {
    setMenu(false);
    try {
      await patch(`/api/conversas/${id}/opcoes`, corpo);
      toast(msgOk);
    } catch (e) {
      toast((e as Error).message, 'erro');
    }
  }

  const visiveis = c.mensagens.filter((m) => !m.interna);
  const podeEnviar = minha && janelaAbertaAgora(c.ultimaMensagemClienteEm);

  return (
    <>
      <main
        className="chat"
        onDragOver={(e) => {
          if (!podeEnviar || !e.dataTransfer.types.includes('Files')) return;
          e.preventDefault();
          setArrastando(true);
        }}
        onDragLeave={(e) => {
          if (e.currentTarget.contains(e.relatedTarget as Node)) return;
          setArrastando(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setArrastando(false);
          if (podeEnviar && e.dataTransfer.files.length) setAnexos([...e.dataTransfer.files]);
        }}
      >
        <header className="chat-topo">
          <Link to={voltarPara} className="icone-btn voltar" aria-label="Voltar para a lista">
            <ArrowLeft />
          </Link>
          <AvatarContato
            id={c.contato.id}
            nome={c.contato.nome}
            fotoUrl={c.contato.fotoUrl}
            tamanho={40}
          />
          <div className="chat-quem">
            <b>{c.contato.nome}</b>
            <span>
              {c.contato.telefone && (
                <span className="num">{formatarTelefone(c.contato.telefone)}</span>
              )}
              <StatusTexto status={c.status} />
              {c.contato.bloqueado && <span className="bloqueado-tag">Bloqueado</span>}
              {c.status === 'atendimento' && c.atendente && !minha && (
                <span>com {c.atendente.nomeExibicao}</span>
              )}
              {c.status === 'fila' && c.motivoTransferencia && <span>{c.motivoTransferencia}</span>}
            </span>
          </div>
          <div className="chat-acoes">
            {(c.status === 'fila' || c.status === 'bot') && (
              <button className="btn primario" onClick={() => acao('assumir', 'Conversa assumida')}>
                <UserCheck /> Assumir
              </button>
            )}
            {c.status === 'atendimento' && podeGerir && (
              <>
                <button className="btn esconde-estreito" onClick={() => setTransferir(true)}>
                  <ArrowRightLeft /> Transferir
                </button>
                <button
                  className="btn esconde-estreito"
                  onClick={() => acao('devolver-ia', 'A IA voltou a responder')}
                >
                  <Bot /> Devolver à IA
                </button>
                <button
                  className="btn primario"
                  onClick={() => acao('finalizar', 'Conversa finalizada')}
                >
                  <CircleCheck /> Finalizar
                </button>
              </>
            )}
            {c.status === 'finalizada' && (
              <button className="btn" onClick={() => acao('reabrir', 'Conversa reaberta')}>
                <RotateCcw /> Reabrir
              </button>
            )}
            {import.meta.env.DEV && c.contato.telefone && (
              <SimuladorCliente telefone={c.contato.telefone} nome={c.contato.nome} compacto />
            )}
            <div className="menu-wrap">
              <button
                className="icone-btn"
                aria-label="Mais opções"
                aria-expanded={menu}
                onClick={() => setMenu((m) => !m)}
              >
                <EllipsisVertical />
              </button>
              {menu && (
                <div className="menu entra" role="menu">
                  {c.status === 'atendimento' && podeGerir && (
                    <>
                      <button
                        role="menuitem"
                        className="so-estreito"
                        onClick={() => (setMenu(false), setTransferir(true))}
                      >
                        <ArrowRightLeft /> Transferir
                      </button>
                      <button
                        role="menuitem"
                        className="so-estreito"
                        onClick={() => (
                          setMenu(false),
                          acao('devolver-ia', 'A IA voltou a responder')
                        )}
                      >
                        <Bot /> Devolver à IA
                      </button>
                    </>
                  )}
                  <button
                    role="menuitem"
                    onClick={() =>
                      opcao(
                        { fixada: !c.fixada },
                        c.fixada ? 'Conversa desafixada' : 'Conversa fixada',
                      )
                    }
                  >
                    {c.fixada ? <PinOff /> : <Pin />}{' '}
                    {c.fixada ? 'Desafixar conversa' : 'Fixar conversa'}
                  </button>
                  <button
                    role="menuitem"
                    onClick={() =>
                      opcao(
                        { silenciada: !c.silenciada },
                        c.silenciada ? 'Avisos ligados' : 'Conversa silenciada',
                      )
                    }
                  >
                    {c.silenciada ? <Bell /> : <BellOff />}{' '}
                    {c.silenciada ? 'Ligar avisos' : 'Silenciar'}
                  </button>
                  <button
                    role="menuitem"
                    onClick={() => opcao({ naoLida: true }, 'Marcada como não lida')}
                  >
                    <Mail /> Marcar como não lida
                  </button>
                  <hr />
                  <button role="menuitem" onClick={() => (setMenu(false), setModal('editar'))}>
                    <Pencil /> Editar contato
                  </button>
                  <button role="menuitem" onClick={() => (setMenu(false), setModal('etiquetas'))}>
                    <Tag /> Etiquetas
                  </button>
                  <a
                    role="menuitem"
                    href={`/api/conversas/${c.id}/exportar`}
                    onClick={() => setMenu(false)}
                  >
                    <Download /> Exportar conversa
                  </a>
                  <hr />
                  {eu.papel === 'admin' && (
                    <button
                      role="menuitem"
                      className="perigo"
                      onClick={() => (setMenu(false), setModal('apagar'))}
                    >
                      <Trash2 /> Apagar conversa
                    </button>
                  )}
                  <button
                    role="menuitem"
                    className={c.contato.bloqueado ? '' : 'perigo'}
                    onClick={() => (setMenu(false), setModal('bloquear'))}
                  >
                    <Ban /> {c.contato.bloqueado ? 'Desbloquear contato' : 'Bloquear contato'}
                  </button>
                </div>
              )}
            </div>
            <button
              className="icone-btn painel-toggle"
              aria-label="Painel do contato"
              aria-pressed={painel}
              onClick={() => setPainel((p) => !p)}
            >
              <PanelRight />
            </button>
          </div>
        </header>

        <div
          className="mensagens"
          role="log"
          aria-live="polite"
          onLoadCapture={(e) => {
            const el = e.currentTarget;
            if (el.scrollHeight - el.scrollTop - el.clientHeight < 400)
              fimRef.current?.scrollIntoView({ block: 'end' });
          }}
        >
          {visiveis.map((m, i) => {
            const anterior = visiveis[i - 1];
            const novoDia =
              !anterior ||
              new Date(anterior.enviadoEm).toDateString() !== new Date(m.enviadoEm).toDateString();
            return (
              <Fragment key={m.id}>
                {novoDia && <span className="dia">{rotuloDia(m.enviadoEm)}</span>}
                <Balao
                  m={m}
                  citada={
                    m.respondeAId ? c.mensagens.find((x) => x.id === m.respondeAId) : undefined
                  }
                  podeResponder={podeEnviar && m.autor !== 'sistema'}
                  aoResponder={() => setRespondendo(m)}
                />
              </Fragment>
            );
          })}
          <div ref={fimRef} />
        </div>

        <Compositor
          conversa={c}
          eu={eu}
          respondendo={respondendo}
          cancelarResposta={() => setRespondendo(null)}
          anexar={setAnexos}
        />

        {arrastando && (
          <div className="soltar-aqui" aria-hidden>
            Solte para enviar
          </div>
        )}
        {anexos && (
          <PreviaEnvio
            conversaId={c.id}
            arquivos={anexos}
            respondeAId={respondendo?.id}
            fechar={() => setAnexos(null)}
            aoEnviar={() => setRespondendo(null)}
          />
        )}
      </main>

      <PainelContato
        conversa={c}
        eu={eu}
        aberto={painel}
        fechar={() => setPainel(false)}
        editar={() => setModal('editar')}
        etiquetas={() => setModal('etiquetas')}
      />
      {modal === 'editar' && <ModalEditarContato conversa={c} fechar={() => setModal(null)} />}
      {modal === 'etiquetas' && <ModalEtiquetas conversa={c} fechar={() => setModal(null)} />}
      {modal === 'bloquear' && <ModalBloquear conversa={c} fechar={() => setModal(null)} />}
      {modal === 'apagar' && <ModalApagar conversa={c} fechar={() => setModal(null)} />}
      {transferir && (
        <ModalTransferir
          conversa={c as ConversaDetalheDTO}
          eu={eu}
          fechar={() => setTransferir(false)}
        />
      )}
    </>
  );
}
